import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useLocation } from 'react-router-dom';
import { setReturnUrl } from '@lib/api/auth';
import { AuthSheet } from './AuthSheet';
import { AuthNudge, type AuthNudgePosition } from './AuthNudge';

export interface OpenAuthSheetOptions {
  returnTo: string;
  reason?: string;
  onAuthenticated?: () => void;
}

export interface ShowAuthNudgeOptions extends OpenAuthSheetOptions {
  message: string;
  position: AuthNudgePosition;
  actionLabel?: string;
}

interface AuthOverlayContextValue {
  openAuthSheet: (options: OpenAuthSheetOptions) => void;
  closeAuthSheet: () => void;
  showAuthNudge: (options: ShowAuthNudgeOptions) => void;
  hideAuthNudge: () => void;
  isAuthSheetOpen: boolean;
}

const AuthOverlayContext = createContext<AuthOverlayContextValue | undefined>(
  undefined
);

export const AuthOverlayProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const location = useLocation();

  const [isAuthSheetOpen, setIsAuthSheetOpen] = useState(false);
  const [reason, setReason] = useState<string | undefined>(undefined);
  const [nudge, setNudge] = useState<{
    message: string;
    position: AuthNudgePosition;
    actionLabel: string;
    sheet: OpenAuthSheetOptions;
  } | null>(null);
  const onAuthenticatedRef = useRef<(() => void) | undefined>(undefined);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  const closeAuthSheet = useCallback((): void => {
    setIsAuthSheetOpen(false);
    setReason(undefined);
    onAuthenticatedRef.current = undefined;

    // Restore focus to the invoking element (if it still exists)
    const el = previousFocusRef.current;
    previousFocusRef.current = null;
    if (el && typeof el.focus === 'function') {
      // Delay one tick so the sheet unmount completes
      setTimeout(() => el.focus(), 0);
    }
  }, []);

  const openAuthSheet = useCallback((options: OpenAuthSheetOptions): void => {
    previousFocusRef.current =
      (document.activeElement as HTMLElement | null) ?? null;

    setReturnUrl(options.returnTo);
    setReason(options.reason);
    onAuthenticatedRef.current = options.onAuthenticated;
    setIsAuthSheetOpen(true);
  }, []);

  const hideAuthNudge = useCallback((): void => {
    setNudge(null);
  }, []);

  const showAuthNudge = useCallback((options: ShowAuthNudgeOptions): void => {
    setNudge({
      message: options.message,
      position: options.position,
      actionLabel: options.actionLabel ?? 'Sign in',
      sheet: {
        returnTo: options.returnTo,
        reason: options.reason,
        onAuthenticated: options.onAuthenticated
      }
    });
  }, []);

  // Close sheet on route changes to avoid stale overlays.
  useEffect(() => {
    if (!isAuthSheetOpen) return;
    closeAuthSheet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const value = useMemo<AuthOverlayContextValue>(
    () => ({
      openAuthSheet,
      closeAuthSheet,
      showAuthNudge,
      hideAuthNudge,
      isAuthSheetOpen
    }),
    [
      closeAuthSheet,
      hideAuthNudge,
      isAuthSheetOpen,
      openAuthSheet,
      showAuthNudge
    ]
  );

  return (
    <AuthOverlayContext.Provider value={value}>
      {children}
      <AuthNudge
        isOpen={Boolean(nudge)}
        message={nudge?.message ?? ''}
        actionLabel={nudge?.actionLabel ?? 'Sign in'}
        position={nudge?.position ?? { x: 0, y: 0 }}
        onClose={hideAuthNudge}
        onAction={() => {
          if (!nudge) return;
          const sheet = nudge.sheet;
          hideAuthNudge();
          openAuthSheet(sheet);
        }}
      />
      <AuthSheet
        isOpen={isAuthSheetOpen}
        reason={reason}
        onClose={closeAuthSheet}
        onAuthenticated={() => {
          const cb = onAuthenticatedRef.current;
          closeAuthSheet();
          cb?.();
        }}
      />
    </AuthOverlayContext.Provider>
  );
};

export const useAuthOverlay = (): AuthOverlayContextValue => {
  const ctx = useContext(AuthOverlayContext);
  if (!ctx) {
    throw new Error('useAuthOverlay must be used within AuthOverlayProvider');
  }
  return ctx;
};

export default AuthOverlayProvider;
